import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { formatFileSize as formatFileSizeFromLib } from "@/lib/format";
import { invalidateFor } from "@/lib/cache-registry";

export interface ProjectDocument {
  id: string;
  project_id: string;
  file_name: string;
  file_size: number | null;
  file_path: string;
  mime_type: string | null;
  uploaded_by: string | null;
  comment_id: string | null;
  created_at: string;
  deleted_at: string | null;
  user?: { full_name: string; avatar_url: string | null } | null;
}

export function useProjectDocuments(projectId: string | undefined) {
  const query = useQuery({
    queryKey: ["project-documents", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_documents")
        .select("*, users!project_documents_uploaded_by_fkey(full_name, avatar_url)")
        .eq("project_id", projectId!)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      type DocumentRow = ProjectDocument & { users: { full_name: string; avatar_url: string | null } | null };
      return ((data ?? []) as unknown as DocumentRow[]).map((d) => ({
        ...d,
        user: d.users ?? null,
      }));
    },
  });

  // Realtime: sentralisert via useRealtimeListeners (REALTIME_TABLES).
  // CACHE_REGISTRY mapper project_documents → ["project-documents", projectId].

  return query;
}

export interface UploadDocumentInput {
  projectId: string;
  file: File;
  uploadedById: string;
  commentId?: string | null;
}

/**
 * Uploads one file to the `project-documents` bucket and inserts the
 * matching `project_documents` row. Shared by the single- and multi-file
 * mutations below so the storage path and row shape stay identical.
 */
async function uploadProjectDocument(input: UploadDocumentInput): Promise<void> {
  const fileExt = input.file.name.split(".").pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
  const filePath = `${input.projectId}/${fileName}`;

  // Upload to storage
  const { error: uploadError } = await supabase.storage
    .from("project-documents")
    .upload(filePath, input.file);

  if (uploadError) throw uploadError;

  // Insert document record
  const { error: insertError } = await supabase
    .from("project_documents")
    .insert({
      project_id: input.projectId,
      file_name: input.file.name,
      file_size: input.file.size,
      file_path: filePath,
      mime_type: input.file.type,
      uploaded_by: input.uploadedById,
      comment_id: input.commentId || null,
    });

  if (insertError) throw insertError;
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadProjectDocument,
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "project_documents", { projectId: vars.projectId });
      toast.success("Dokument lastet opp");
    },
    onError: (err: Error) => {
      console.error("Upload error:", err);
      toast.error(`Feil ved opplasting: ${err.message}`);
    },
  });
}

export interface UploadDocumentsInput {
  projectId: string;
  files: File[];
  uploadedById: string;
  /** Called before each file starts: (1-based index, total). */
  onProgress?: (index: number, total: number) => void;
}

export interface UploadDocumentsResult {
  uploaded: number;
  failed: Array<{ fileName: string; message: string }>;
}

/**
 * VIB-2869: uploads several files in sequence (dra-og-slipp / multi-select on
 * the Dokumenter tab). One file failing does not stop the rest; the result
 * lists what failed so the caller can show it. Cache is invalidated once at
 * the end and the user gets a single toast instead of one per file.
 */
export function useUploadDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadDocumentsInput): Promise<UploadDocumentsResult> => {
      const result: UploadDocumentsResult = { uploaded: 0, failed: [] };
      for (const [i, file] of input.files.entries()) {
        input.onProgress?.(i + 1, input.files.length);
        try {
          await uploadProjectDocument({
            projectId: input.projectId,
            file,
            uploadedById: input.uploadedById,
          });
          result.uploaded += 1;
        } catch (err) {
          // Supabase feil er objekter med `message`, ikke alltid Error-instanser.
          const message =
            typeof err === "object" && err !== null && "message" in err
              ? String((err as { message: unknown }).message)
              : String(err);
          console.error("Upload error:", file.name, err);
          result.failed.push({ fileName: file.name, message });
        }
      }
      return result;
    },
    onSuccess: async (result, vars) => {
      if (result.uploaded > 0) {
        await invalidateFor(qc, "project_documents", { projectId: vars.projectId });
        toast.success(
          result.uploaded === 1 ? "Dokument lastet opp" : `${result.uploaded} dokumenter lastet opp`,
        );
      }
      for (const f of result.failed) {
        toast.error(`Kunne ikke laste opp ${f.fileName}: ${f.message}`);
      }
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; projectId: string }) => {
      const { error } = await supabase
        .from("project_documents")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "project_documents", { projectId: vars.projectId });
      toast.success("Dokument slettet");
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}

export function getDocumentUrl(filePath: string): string {
  // External URLs (e.g. Monday.com imports) are used directly
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }
  return supabase.storage.from("project-documents").getPublicUrl(filePath).data
    .publicUrl;
}

/**
 * Re-export for bakoverkompatibilitet. Bruk `formatFileSize` fra
 * `@/lib/format` direkte i nye filer (VIB-691).
 */
export const formatFileSize = formatFileSizeFromLib;
